import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Save, Edit2, Type, AlertCircle, Plus, X, Upload, Trash2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContentEditor = () => {
  const { getAuthHeader } = useAuth();
  const { toast } = useToast();
  const [pageContent, setPageContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingContent, setEditingContent] = useState({});
  const [saving, setSaving] = useState({});
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/page-content`);
      setPageContent(response.data);
      
      // Initialize editing state
      const initialEdit = {};
      response.data.forEach(item => {
        initialEdit[item.id] = { ...item.content };
      });
      setEditingContent(initialEdit);
    } catch (error) {
      console.error('Failed to fetch page content:', error);
      toast({
        title: "Error",
        description: "Failed to load content",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (contentId, field, value) => {
    setEditingContent(prev => ({
      ...prev,
      [contentId]: {
        ...prev[contentId],
        [field]: value
      }
    }));
  };

  const handleArrayItemChange = (contentId, field, index, value) => {
    setEditingContent(prev => {
      const newArray = [...(prev[contentId][field] || [])];
      newArray[index] = value;
      return {
        ...prev,
        [contentId]: {
          ...prev[contentId],
          [field]: newArray
        }
      };
    });
  };

  const handleAddArrayItem = (contentId, field) => {
    setEditingContent(prev => {
      const newArray = [...(prev[contentId][field] || []), ''];
      return {
        ...prev,
        [contentId]: {
          ...prev[contentId],
          [field]: newArray
        }
      };
    });
  };

  const handleRemoveArrayItem = (contentId, field, index) => {
    setEditingContent(prev => {
      const newArray = [...(prev[contentId][field] || [])];
      newArray.splice(index, 1);
      return {
        ...prev,
        [contentId]: {
          ...prev[contentId],
          [field]: newArray
        }
      };
    });
  };

  const handleImageUpload = async (contentId, field, file) => {
    if (!file) return;

    try {
      setUploading(prev => ({ ...prev, [`${contentId}-${field}`]: true }));

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/admin/upload`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Update with backend URL
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      handleContentChange(contentId, field, imageUrl);

      toast({
        title: "Success",
        description: "Image uploaded successfully"
      });
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive"
      });
    } finally {
      setUploading(prev => ({ ...prev, [`${contentId}-${field}`]: false }));
    }
  };

  const handleImageDelete = async (contentId, field, imageUrl) => {
    if (!imageUrl) return;

    try {
      const filename = imageUrl.split('/').pop();
      await axios.delete(
        `${API}/admin/upload/${filename}`,
        { headers: getAuthHeader() }
      );

      handleContentChange(contentId, field, '');

      toast({
        title: "Success",
        description: "Image deleted successfully"
      });
    } catch (error) {
      console.error('Failed to delete image:', error);
      // Still clear the field even if delete fails
      handleContentChange(contentId, field, '');
    }
  };

  const saveContent = async (contentId) => {
    try {
      setSaving(prev => ({ ...prev, [contentId]: true }));
      await axios.put(
        `${API}/admin/page-content/${contentId}`,
        { content: editingContent[contentId] },
        { headers: getAuthHeader() }
      );
      
      toast({
        title: "Success",
        description: "Content updated successfully"
      });
      
      fetchPageContent();
    } catch (error) {
      console.error('Failed to save content:', error);
      toast({
        title: "Error",
        description: "Failed to save content",
        variant: "destructive"
      });
    } finally {
      setSaving(prev => ({ ...prev, [contentId]: false }));
    }
  };

  const groupedContent = pageContent.reduce((acc, item) => {
    if (!acc[item.page_name]) {
      acc[item.page_name] = [];
    }
    acc[item.page_name].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <p>Loading content editor...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="heading-1">Content Editor</h1>
          <p className="body-large">Edit all text content on your website</p>
        </div>
        <div className="info-banner">
          <AlertCircle size={16} />
          <span className="caption">Changes will reflect on the public website immediately after saving</span>
        </div>
      </div>

      <div className="content-editor-wrapper">
        {Object.entries(groupedContent).map(([pageName, contents]) => (
          <div key={pageName} className="content-page-group">
            <div className="page-group-header">
              <Type size={24} className="page-icon" />
              <h2 className="heading-2">
                {pageName.charAt(0).toUpperCase() + pageName.slice(1)} Page
              </h2>
            </div>

            <div className="content-sections-grid">
              {contents.map(content => (
                <div key={content.id} className="content-edit-card">
                  <div className="card-header">
                    <div className="card-title">
                      <Edit2 size={18} />
                      <h3 className="heading-3">
                        {content.section_name.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </h3>
                    </div>
                  </div>

                  <div className="card-body">
                    {Object.entries(editingContent[content.id] || {}).map(([field, value]) => (
                      <div key={field} className="form-field">
                        <label className="field-label">
                          {field.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </label>
                        
                        {Array.isArray(value) ? (
                          <div className="array-field">
                            {value.map((item, index) => (
                              <div key={index} className="array-item">
                                <input
                                  type="text"
                                  value={item}
                                  onChange={(e) => handleArrayItemChange(content.id, field, index, e.target.value)}
                                  className="field-input array-input"
                                  placeholder={`Enter ${field.replace(/_/g, ' ')} ${index + 1}...`}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemoveArrayItem(content.id, field, index)}
                                  className="btn-icon-danger"
                                  title="Remove item"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => handleAddArrayItem(content.id, field)}
                              className="btn-add-item"
                            >
                              <Plus size={16} />
                              Add {field.replace(/_/g, ' ').slice(0, -1)}
                            </button>
                          </div>
                        ) : field.includes('opacity') ? (
                          <div className="opacity-field">
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.05"
                              value={value}
                              onChange={(e) => handleContentChange(content.id, field, parseFloat(e.target.value))}
                              className="opacity-slider"
                            />
                            <span className="opacity-value">{Math.round(value * 100)}%</span>
                          </div>
                        ) : field.includes('image') ? (
                          <div className="image-field">
                            {field.includes('hero') && (
                              <div className="image-specs">
                                <strong>Recommended Specs:</strong> 1920x1080px (16:9) • Landscape • JPG/PNG • Max 5MB
                              </div>
                            )}
                            {field.includes('services') && (
                              <div className="image-specs">
                                <strong>Recommended Specs:</strong> 1920x800px • Landscape • JPG/PNG • Max 5MB
                              </div>
                            )}
                            {field.includes('contact') && (
                              <div className="image-specs">
                                <strong>Recommended Specs:</strong> 1920x900px • Landscape • JPG/PNG • Max 5MB
                              </div>
                            )}
                            <div className="image-upload-container">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) handleImageUpload(content.id, field, file);
                                }}
                                className="file-input"
                                id={`file-${content.id}-${field}`}
                                disabled={uploading[`${content.id}-${field}`]}
                              />
                              <label htmlFor={`file-${content.id}-${field}`} className="file-label">
                                <Upload size={18} />
                                {uploading[`${content.id}-${field}`] ? 'Uploading...' : 'Upload Image'}
                              </label>
                              {value && (
                                <button
                                  type="button"
                                  onClick={() => handleImageDelete(content.id, field, value)}
                                  className="btn-icon-danger"
                                  title="Delete image"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                            {value ? (
                              <div className="image-preview">
                                <img src={value} alt="Preview" onError={(e) => e.target.style.display = 'none'} />
                                <p className="caption image-url">{value}</p>
                              </div>
                            ) : (
                              <div className="no-image-placeholder">
                                <p className="caption">No background image (using default gradient)</p>
                              </div>
                            )}
                          </div>
                        ) : field.includes('subtitle') || field.includes('description') ? (
                          <textarea
                            value={value}
                            onChange={(e) => handleContentChange(content.id, field, e.target.value)}
                            className="field-input textarea-input"
                            rows="4"
                            placeholder={`Enter ${field.replace(/_/g, ' ')}...`}
                          />
                        ) : (
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => handleContentChange(content.id, field, e.target.value)}
                            className="field-input"
                            placeholder={`Enter ${field.replace(/_/g, ' ')}...`}
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="card-footer">
                    <button
                      onClick={() => saveContent(content.id)}
                      className="btn-primary save-btn"
                      disabled={saving[content.id]}
                    >
                      <Save size={18} />
                      {saving[content.id] ? 'Saving...' : 'Save Changes'}
                    </button>
                    {content.updated_by && (
                      <p className="caption meta-info">
                        Last updated by <strong>{content.updated_by}</strong> on {new Date(content.updated_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContentEditor;
